import type { BookableOptionValue } from "@@/shared/acquire/resource-reference-options/bookable-option-value";
import type { ResourceReservationRequest } from "@@/shared/acquire/resources/resource-reservation-request";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
    type IsoAndUnixTimestamp,
} from "@@/shared/date-time/iso-and-unix";
import { idZodSchema } from "@@/shared/id";
import {
    contactInfoZodObject,
    type ContactInfo,
} from "@@/shared/locations/contact-info";
import {
    orderGroupIdZodSchema,
    type OrderGroupId,
} from "@@/shared/order-groups/order-group-id";
import type {
    AccommodationPurpose} from "@@/shared/orders/order-accommodation.target-group";
import {
    accommodationPurposeZodSchema,
} from "@@/shared/orders/order-accommodation.target-group";
import { orderIdZodSchema, type OrderId } from "@@/shared/orders/order-id";
import { generateId } from "@@/utilities/generate-id";
import type { Except } from "type-fest";
import { z } from "zod";
import { cartZodSchema, type Cart } from "../../carts/cart_old";

// REQUEST
type CheckoutCommand = {
    readonly _id: string;
    readonly _type: "CHECKOUT_COMMAND";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly cart: Cart;
    readonly checkoutId: string;
    readonly contactInfo: ContactInfo | null;
    readonly onPremiseBooking?: boolean;
    readonly accommodationPurpose?: AccommodationPurpose;
    readonly leaveMyStuffOutside?: boolean;

    // forceReservation throws error if
    // not the picked resources are available
    readonly forceReservation?: boolean;
    readonly reservations: ResourceReservationRequest[];
    readonly optionValues: BookableOptionValue[];
};

type CheckoutCommandFactoryParams = Except<
    CheckoutCommand,
    "_id" | "_type" | "_createdAt"
>;
const checkoutCommandFactory = (params: CheckoutCommandFactoryParams) => {
    const checkoutCommand: CheckoutCommand = {
        ...params,
        _id: generateId({ prefix: "checkout_command__" }),
        _type: "CHECKOUT_COMMAND",
        _createdAt: nowIsoAndUnix(),
    };
    return checkoutCommand;
};

const checkoutCommandZodSchema: z.ZodSchema<CheckoutCommand> = z.object({
    _id: idZodSchema,
    _type: z.literal("CHECKOUT_COMMAND"),
    _createdAt: isoAndUnixZodSchema,
    checkoutId: idZodSchema,
    contactInfo: contactInfoZodObject.nullable(),
    cart: cartZodSchema,
    accommodationPurpose: accommodationPurposeZodSchema.optional(),
    leaveMyStuffOutside: z.boolean().default(false),
    onPremiseBooking: z.boolean().default(false),
    forceReservation: z.boolean().default(false),
    reservations: z.array(z.any()), // todo: add resource reservation request schemas
    optionValues: z.array(z.any()), // todo: add bookable option value schemas
});

export {
    checkoutCommandFactory,
    checkoutCommandFactoryResponse,
    checkoutCommandResponseZodSchema,
    checkoutCommandZodSchema,
};
export type {
    CheckoutCommand,
    CheckoutCommandFactoryParams,
    CheckoutCommandResponse,
};

// RESPONSE
type CheckoutCommandResponse = {
    readonly _id: string;
    readonly _type: "CHECKOUT_COMMAND_RESPONSE";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly checkoutId: string;
    readonly orderGroupId: OrderGroupId;
    readonly orderId: OrderId;
    readonly cartHash: string;
};

type CheckoutCommandFactoryResponseProps = Except<
    CheckoutCommandResponse,
    "_id" | "_type" | "_createdAt"
>;
const checkoutCommandFactoryResponse = (
    params: CheckoutCommandFactoryResponseProps,
) => {
    const orderGroupCheckoutResponse: CheckoutCommandResponse = {
        ...params,
        _id: generateId({ prefix: "checkout_command_response__" }),
        _type: "CHECKOUT_COMMAND_RESPONSE",
        _createdAt: nowIsoAndUnix(),
    };
    return orderGroupCheckoutResponse;
};

const checkoutCommandResponseZodSchema: z.ZodSchema<CheckoutCommandResponse> =
    z.object({
        _id: idZodSchema,
        _type: z.literal("CHECKOUT_COMMAND_RESPONSE"),
        _createdAt: isoAndUnixZodSchema,
        checkoutId: idZodSchema,
        orderGroupId: orderGroupIdZodSchema,
        orderId: orderIdZodSchema,
        cartHash: idZodSchema,
    });
